import {
  CARD_CHOICE_SECONDS,
  CARD_REVEAL_SECONDS,
  MAX_QUIET_TURNS,
  type BoardDef,
  type CharacterDef,
  type Roster,
} from '@archon3d/core';

export interface RulePage {
  title: string;
  lines: string[];
}

export const RULE_PAGES: readonly RulePage[] = [
  {
    title: 'Quest viewpoint',
    lines: [
      'Board/Card view: left stick moves along your gaze or sideways in your current view, including vertically.',
      'Right stick: a flick left or right turns you by a step, level with the floor (Settings, Turning: 30°, 45° or 90°). Set to Free, it turns smoothly toward any side of your view, through full loops.',
      'Travel stays within 3 metres of the table centre and 0.2–4 metres above the floor. Physical head tracking stays active.',
      'Move gently. A/X, either stick click or Reset view returns to your original upright seat. Arena fighter controls are separate.',
    ],
  },
  {
    title: 'How to win',
    lines: [
      'Move one piece on your turn. Hold every power point or eliminate every rival faction.',
      'Movement follows each character range and type: ground, flying or teleporting.',
      'A contested cell is settled by the combat mode chosen when the game was created.',
      'On your turn you may offer a draw, once a turn. The other sides answer Accept or Decline, and the AI accepts only when it is not ahead. Playing on takes the offer off.',
      `${MAX_QUIET_TURNS} turns in a row with no fight, Heal or Revive are a draw; a summoned elemental's fight counts, other spells do not. The game sign counts the last ten down.`,
    ],
  },
  {
    title: 'Turns & power',
    lines: [
      'Select a piece, then a highlighted destination. Power-point ownership changes the balance.',
      'Luminosity changes through the round and strengthens the side favoured by the current light. A neutral side draws the same +4 on every square.',
      'Imprison holds a Light or Dark piece until its side’s peak of the cycle, a neutral one for five of its turns. Curse slows its victim in every fight.',
      'The action tablet shows legal spells, magic points, danger and the current turn.',
    ],
  },
  {
    title: 'Combat modes',
    lines: [
      'Card: resolve on the hex card board; the default for a new game. Roll: seeded dice around the character odds.',
      'Arena: control the fighter directly. Watch: both fighters are controlled by AI.',
      'Human-friendly AI is On by default in New Game and Host Game. Against a human Arena fighter only, AI reacts 100 ms later, has 25% wider aiming spread, moves 10% slower and uses 20% less target prediction. Turn it Off before starting for unassisted AI. The host chooses for the whole room. Character stats, AI-versus-AI, Watch, Card and Roll are unchanged.',
      'Arena controls: either stick moves; with both pushed, the left moves and the right aims. Hold fire to aim a Wizard or Sorceress shot; while it flies, right-stick left/right bends it. Hold B or Y (on a controller, Start) to give the duel up.',
      'In the arena a blow, an aura or a burst lands a quarter of a second after it is pressed, with a short lunge: the ring round the fighter burns orange meanwhile, and a quick dash gets out of its way. A shot leaves at once. The ring fills again as the weapon reloads.',
      'Lava is ground a walker can cross, and it burns: a hit point a second, less by the walker’s resistance to fire. A flier passes over it unharmed.',
    ],
  },
  {
    title: 'Card combat',
    lines: [
      'Your fighting character is played automatically. Choose one legal support, or save it and pass.',
      `You have ${CARD_CHOICE_SECONDS} seconds to choose, counted down over the cards and on the tablet; when they run out the fight goes on without your support. Then the clash is told: each support comes down, the chances are rolled, and the winner is named; the result stays up until you press Continue (or Enter), for ${CARD_REVEAL_SECONDS} seconds at most in a hosted game.`,
      'Support: +1 base, +2 instead with two other available providers; +1 for fit; +2 for a role counter. Maximum +4.',
      'Protection counters Pressure, Pressure counters Deception, Deception counters Protection. Each support point multiplies winning odds by 1.2. Outcomes are seeded and probabilistic, not guaranteed by the highest rating; survivors carry wounds back to the board.',
      'Printed ratings and properties come from this game’s Expert Arena characters. Current wounds, light, curses, counters and difficulty change matchup odds, not the printed Expert rating. Edited characters without matching measurements use a property estimate.',
    ],
  },
  {
    title: 'Good play',
    lines: [
      'Protect spellcasters and power points; do not trade a leader for a weak isolated piece.',
      'Use flying and teleporting pieces to threaten routes that ground pieces cannot take.',
      'Check the threat warning before committing, and save magic for a board-changing moment.',
    ],
  },
  {
    title: 'Games & online',
    lines: [
      'New Game starts locally. Host creates a room code; Join uses that code on another device.',
      'Each player controls one faction. The host chooses seats, AI levels and allowed combat modes.',
      'Game Storage saves private games; the editor creates boards, arenas, characters and rules.',
    ],
  },
  {
    title: 'Account & sound',
    lines: [
      'Register and verify email to publish games, use private cloud saves and join the leaderboard.',
      'Recommend to a friend sends an invitation from the menu.',
      'Radio is personal to this device. Music and Sound sliders work in Settings and during play.',
    ],
  },
];

/** A rule page by its title; a title that is not there fails loudly rather than showing another page. */
export function rulePage(title: string): RulePage {
  const page = RULE_PAGES.find((p) => p.title === title);
  if (!page) throw new Error(`No rule page '${title}'`);
  return page;
}

/**
 * The pages each section of the 2D rule book prints, by title. It picked them by index, and the
 * index drifted as pages were added: three sections printed their neighbours' pages.
 */
export const RULE_BOOK_SECTIONS = {
  // The rules come first on a screen; the headset's own controls close the section there, where
  // the headset's tablet opens with them (x-player-journey#12).
  play: ['How to win', 'Turns & power', 'Combat modes', 'Card combat', 'Quest viewpoint'],
  tips: 'Good play',
  start: 'Games & online',
  account: 'Account & sound',
} as const;

/** Only a spellcaster's projectile can be bent in flight (the original Wizard and Sorceress). */
export function attackLabel(character: CharacterDef): string {
  return character.attack.type === 'projectile' && character.spellcaster === true
    ? 'steered projectile'
    : character.attack.type;
}

export function characterRule(character: CharacterDef): string {
  const attack = `${attackLabel(character)} ${Math.round(character.attack.damage)}`;
  const move = `${character.movement.type} ${character.movement.range}`;
  const magic = character.spellPoints ? ` · magic ${character.spellPoints}` : '';
  const summoned = character.summoned ? ' · summoned' : '';
  return `${character.name} · power ${character.force ?? '—'} · ${Math.round(character.hitPoints)} HP · ${move} · ${attack}${magic}${summoned}`;
}

/**
 * The reading order of the character pages: tier 1 up to tier 5; within a tier the counterparts
 * of the two armies side by side, the first faction's piece first, from the weakest pair to the
 * strongest, then anything without a counterpart, and the summoned elements last.
 *
 * Counterparts are read off the board: the pieces that start on mirrored squares and share a
 * tier. The Phoenix and the Dragon mirror each other but sit in different tiers, so each is
 * listed under its own tier. A board with more or fewer than two factions pairs nothing.
 */
export function rulebookOrder(roster: Roster, board?: BoardDef): CharacterDef[] {
  const partnerOf = counterparts(roster, board);
  const seat = factionRank(board);
  const force = (c: CharacterDef) => c.force ?? 0;
  const groups: { tier: number; rank: number; members: CharacterDef[] }[] = [];
  const placed = new Set<string>();
  for (const c of Object.values(roster)) {
    if (placed.has(c.id)) continue;
    const partner = partnerOf.get(c.id);
    const members = partner ? [c, roster[partner]!] : [c];
    members.sort(
      (a, b) => (seat.get(a.id) ?? 9) - (seat.get(b.id) ?? 9) || a.name.localeCompare(b.name),
    );
    for (const m of members) placed.add(m.id);
    const mean = members.reduce((sum, m) => sum + force(m), 0) / members.length;
    groups.push({ tier: c.tier, rank: (c.summoned ? 1000 : 0) + mean, members });
  }
  groups.sort(
    (a, b) =>
      a.tier - b.tier || a.rank - b.rank || a.members[0]!.name.localeCompare(b.members[0]!.name),
  );
  return groups.flatMap((g) => g.members);
}

/** The faction each character starts with, as an index into the board's faction order. */
function factionRank(board?: BoardDef): Map<string, number> {
  const rank = new Map<string, number>();
  if (!board) return rank;
  for (const p of board.placements) {
    const i = board.factions.findIndex((f) => f.id === p.faction);
    if (i >= 0 && !rank.has(p.character)) rank.set(p.character, i);
  }
  return rank;
}

/** Mirrored starting squares of a two-faction board, by character id, in both directions. */
function counterparts(roster: Roster, board?: BoardDef): Map<string, string> {
  const pairs = new Map<string, string>();
  if (!board || board.factions.length !== 2) return pairs;
  const at = new Map<string, BoardDef['placements'][number]>(
    board.placements.map((p) => [`${p.x},${p.y}`, p]),
  );
  const votes = new Map<string, Map<string, number>>();
  for (const p of board.placements) {
    const mine = roster[p.character];
    if (!mine) continue;
    for (const key of [`${board.width - 1 - p.x},${p.y}`, `${p.x},${board.height - 1 - p.y}`]) {
      const q = at.get(key);
      const theirs = q && roster[q.character];
      if (!q || !theirs || q.faction === p.faction || q.character === p.character) continue;
      if (theirs.tier !== mine.tier || !!theirs.summoned !== !!mine.summoned) continue;
      const row = votes.get(p.character) ?? new Map<string, number>();
      row.set(q.character, (row.get(q.character) ?? 0) + 1);
      votes.set(p.character, row);
    }
  }
  const favourite = (id: string): string | undefined => {
    const row = votes.get(id);
    if (!row) return undefined;
    return [...row.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]![0];
  };
  for (const id of votes.keys()) {
    const other = favourite(id);
    if (other && favourite(other) === id) pairs.set(id, other);
  }
  return pairs;
}

/** Compact pages that fit the wooden Quest tablet; the full book remains available in 2D. */
export function tabletRulePages(roster: Roster, board?: BoardDef): RulePage[] {
  const characters = rulebookOrder(roster, board);
  const pages = [...RULE_PAGES];
  for (let i = 0; i < characters.length; i += 3) {
    pages.push({
      title: `Characters ${i + 1}-${Math.min(i + 3, characters.length)}`,
      lines: characters.slice(i, i + 3).map(characterRule),
    });
  }
  return pages;
}
